import { Check, Languages } from "lucide-react";
import { LOCALE_ORDER, localeMeta, type Locale, useI18n } from "../i18n";

interface LanguageSelectorProps {
  variant?: "compact" | "panel";
  className?: string;
}

export function LanguageSelector({
  variant = "compact",
  className = "",
}: LanguageSelectorProps) {
  const { locale, setLocale, t } = useI18n();

  if (variant === "panel") {
    return (
      <div
        className={`rounded-2xl border border-[var(--border-subtle)] bg-white p-4 ${className}`}
      >
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-[var(--accent-light)] p-2.5 text-[var(--accent)]">
            <Languages className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-black text-[var(--text-primary)]">
              {t("settings.languageCardTitle")}
            </div>
            <p className="mt-1 text-xs leading-5 text-[var(--text-secondary)]">
              {t("settings.languageCardText")}
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {LOCALE_ORDER.map((code) => {
            const active = locale === code;
            const meta = localeMeta[code];
            return (
              <button
                key={code}
                type="button"
                onClick={() => setLocale(code)}
                className="flex items-center justify-between rounded-2xl border px-3 py-2.5 text-start transition-all hover:shadow-sm"
                style={{
                  borderColor: active
                    ? "rgba(215,15,100,0.22)"
                    : "var(--border-subtle)",
                  background: active ? "var(--accent-light)" : "var(--surface)",
                }}
              >
                <span>
                  <span className="block text-sm font-black text-[var(--text-primary)]">
                    {meta.nativeName}
                  </span>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
                    {meta.englishName}
                  </span>
                </span>
                {active ? (
                  <Check className="w-4 h-4 text-[var(--accent)]" />
                ) : (
                  <span className="font-mono text-[10px] font-black text-[var(--text-muted)]">
                    {meta.short}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <label
      className={`inline-flex h-10 items-center gap-2 rounded-full border border-white/30 bg-white/90 px-3 text-[var(--text-primary)] shadow-sm backdrop-blur-xl ${className}`}
    >
      <Languages className="w-4 h-4 text-[var(--accent)]" />
      <span className="sr-only">{t("language.select")}</span>
      <select
        value={locale}
        onChange={(event) => setLocale(event.target.value as Locale)}
        className="h-full border-none bg-transparent text-xs font-black outline-none"
        aria-label={t("language.select")}
      >
        {LOCALE_ORDER.map((code) => (
          <option key={code} value={code}>
            {localeMeta[code].nativeName}
          </option>
        ))}
      </select>
    </label>
  );
}
